/**
 * Calendar of Events — The Chi Alliance
 * Fully interactive prototype:
 * - Calendar grid with event dots
 * - Event list with clickable cards
 * - Event detail modal with registration
 * - Mock sign-in / create account flow
 * - My Events dashboard for registered user
 * Design preservation: Obsidian Pulse dark editorial styling with magenta, gold, and cyan accents.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "wouter";
import Navigation from "@/components/Navigation";
import SectionBadge from "@/components/SectionBadge";
import Footer from "@/components/Footer";
import MembershipBanner from "@/components/MembershipBanner";
import CtaIcon from "@/components/CtaIcon";
import { chiNotify } from "@/components/ChiNotification";
import { useOptionalAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/_core/hooks/useAuth";
import { useEventClock } from "@/hooks/useEventClock";
import { EVENTS as CANONICAL_EVENTS, type CHIEvent } from "@/lib/eventsData";
import { paidEventCatalogRefetchInterval } from "@/lib/eventCatalogFreshness";
import { getInternalEventRegistrationCreateAccountUrl, getInternalEventRegistrationLoginUrl, getPostRegistrationRedirectUrl } from "@/lib/eventRegistration";
import { useAppNavigation } from "@/lib/useAppNavigation";
import { PENDING_REVIEW_MESSAGE, resolveEventRegistrationAction } from "@/lib/eventRegistrationFlow";
import { getEventTicketsUrl, shouldUseTicketsPage } from "@/lib/eventRegistrationFlow";
import { REGISTRANT_BLOCK_MESSAGES, approvalStatusFromRegistrationState, isApprovalQueueEvent, presentRegisterFailure } from "@/lib/registrationApproval";
import {
  closeReservedCheckoutWindow,
  handoffToCheckout,
  reserveCheckoutWindow,
  type ReservedCheckoutWindow,
} from "@/lib/checkoutWindow";
import { trpc } from "@/lib/trpc";
import { eventFeaturedArtwork } from "@shared/eventPlaceholderArt";
import { resolveSpeakerPortrait, type PublishedSpeakerPortrait } from "@/lib/speakerPortraits";
import SpeakerAvatar from "@/components/SpeakerAvatar";
import PersonPortraitAvatar, { CRM_ROW_PORTRAIT_SIZE } from "@/components/PersonPortraitAvatar";
import FeaturedEventBackground from "@/components/FeaturedEventBackground";
import ResponsiveImage from "@/components/ResponsiveImage";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { type CalendarCostFilter } from "@/lib/calendarPricing";
import BuyTicketsButton from "@/components/BuyTicketsButton";
import { shouldShowBuyTickets, ticketsHref } from "@/lib/eventCta";
import { useMyTicketsBySlug } from "@/hooks/useMyTicketsBySlug";
import { useMyTicketsForEvent } from "@/hooks/useMyTicketsForEvent";
import { useServerNow } from "@/lib/serverClock";
import { useEventPricingModes, useTicketsCheckoutEnabled } from "@/lib/useEventPricingModes";
import { ticketsCheckoutEnabled } from "@/lib/eventCta";
import {
  ALL_MONTHS,
  DEFAULT_CALENDAR_EVENT_FILTERS,
  filterCalendarEvents,
  getCalendarLocationOptions,
  getCalendarMonthOptions,
  getCalendarSpeakerOptions,
  hasActiveCalendarEventFilters,
  isPastCalendarEvent,
  matchesCalendarMonth,
  matchesCalendarSearch,
  resetCalendarEventFilters,
  type CalendarEventTypeFilter,
} from "@/lib/calendarFilters";
import {
  X, Calendar, Clock, MapPin, Users, ChevronLeft, ChevronRight, ChevronDown,
  CheckCircle, User, LogOut, Ticket, ExternalLink, ArrowRight, Eye,
  Video, Building2, Star, Share2, Facebook, Link, Linkedin, Copy, CalendarClock, Filter, RotateCcw,
  Search, LockKeyhole, PlayCircle, GraduationCap, ZoomIn
} from "lucide-react";

/**
 * The "Inside the CHI Experience" strip.
 *
 * Static rather than data-driven: this is editorial framing of what the community
 * feels like, not a listing, and it should not change shape because of what happens
 * to be on the calendar. Each image is generated and cropped to the card's 7:8
 * portrait ratio so the visual composition remains consistent across devices.
 */
const EXPERIENCE_TILES = [
  {
    image: "/media/events/experience/leadership-roundtable.webp",
    alt: "Panel discussion at a CHI Alliance event",
    kicker: "Leadership Roundtables",
    headline: "Peer conversations that move ideas into action.",
    detail: "Honest perspective from people navigating the same decisions.",
  },
  {
    image: "/media/events/experience/networking-salon.webp",
    alt: "Members networking at a CHI Alliance salon",
    kicker: "Networking",
    headline: "Meaningful introductions, not random exchanges.",
    detail: "Meet the people whose experience changes the next conversation.",
  },
  {
    image: "/media/events/experience/expert-voices.webp",
    alt: "A speaker presenting at a CHI Alliance session",
    kicker: "Expert Voices",
    headline: "Practical insight from leaders doing the work.",
    detail: "Leave with the clarity to put a useful idea into practice.",
  },
  {
    image: "/media/events/experience/executive-dinner.webp",
    alt: "Members in conversation over an executive dinner",
    kicker: "Executive Tables",
    headline: "Smaller rooms where candour comes easily.",
    detail: "Built for the kind of exchange that lasts beyond the table.",
  },
] as const;


// ── DATA ──────────────────────────────────────────────────────────────────────
// ── TYPES ─────────────────────────────────────────────────────────────────────
type Event = CHIEvent;
type Modal = "event" | "register" | "signin" | "confirmed" | "myevents" | "recordingGate" | null;

/**
 * Optional full-bleed background treatments for featured events. The sharp image
 * still comes from the event artwork resolver, so managed event records inherit
 * the catalog artwork while this map only needs the wide blurred companion.
 */
function SpeakerFilterAvatar({
  speaker,
  color,
  publishedSpeakers,
}: {
  speaker: string;
  color: string;
  publishedSpeakers: readonly PublishedSpeakerPortrait[];
}) {
  // No event slug: the dropdown lists a person, not a booking, so resolution
  // falls through to matching that person's published portrait by name.
  const portrait = resolveSpeakerPortrait({ eventSlug: "", speakerName: speaker, publishedSpeakers });

  return (
    <SpeakerAvatar
      name={speaker}
      portraitUrl={portrait}
      className="calendar-filter-avatar"
      fallbackClassName="calendar-filter-avatar--fallback"
      fallbackStyle={{ background: `${color}2b`, borderColor: `${color}80` }}
    />
  );
}

function RotatingEventTileSpeaker({
  speakers,
  organization,
  accent,
  paused,
}: {
  speakers: readonly PublishedSpeakerPortrait[];
  organization?: string;
  accent: string;
  paused: boolean;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const speakerKey = speakers.map(speaker => `${speaker.eventSlug}:${speaker.name}`).join("|");

  useEffect(() => {
    setActiveIndex(0);
  }, [speakerKey]);

  useEffect(() => {
    if (
      speakers.length < 2
      || paused
      || window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) return;
    const rotation = window.setInterval(() => {
      setActiveIndex(current => (current + 1) % speakers.length);
    }, 4_000);
    return () => window.clearInterval(rotation);
  }, [paused, speakerKey, speakers.length]);

  const activeSpeaker = speakers[activeIndex] ?? speakers[0];
  if (!activeSpeaker) return null;

  return (
    <div
      className="events-card__speaker-rotation"
      aria-label={speakers.length > 1 ? `Speakers: ${speakers.map(speaker => speaker.name).join(", ")}` : undefined}
    >
      <div
        key={`${activeSpeaker.eventSlug}:${activeSpeaker.name}`}
        className="events-card__speaker-identity"
        aria-hidden={speakers.length > 1 ? "true" : undefined}
      >
        <PersonPortraitAvatar
          name={activeSpeaker.name}
          portraitUrl={activeSpeaker.portraitUrl}
          size={CRM_ROW_PORTRAIT_SIZE}
          accent={accent}
        />
        <span className="events-card__speaker-text">
          <strong>{activeSpeaker.name}</strong>
          {organization && speakers.length === 1 && <small>{organization}</small>}
        </span>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
/** Disabled CTA for a registration waiting on an admin: muted, not-allowed, matching the ended/unavailable states. */
const AWAITING_DECISION_CTA_STYLE: React.CSSProperties = {
  background: "rgba(148,163,184,0.10)",
  border: "1px solid rgba(148,163,184,0.22)",
  color: "#CBD5E1",
  boxShadow: "none",
  cursor: "not-allowed",
  opacity: 0.72,
};

export default function CalendarOfEvents() {
  const navigate = useAppNavigation();
  const { isAuthenticated, user } = useAuth();
  const membershipAuth = useOptionalAuth();
  const hasPaidAccess = membershipAuth?.isPremium ?? false;
  const trpcUtils = trpc.useUtils();
  const managedEventsQuery = trpc.events.publicCatalog.useQuery(undefined, {
    retry: false,
    refetchInterval: paidEventCatalogRefetchInterval,
    refetchOnWindowFocus: true,
  });
  const publishedSpeakersQuery = trpc.speakers.publicList.useQuery(undefined, { retry: false });
  const publishedSpeakers = publishedSpeakersQuery.data ?? [];
  const EVENTS = useMemo<CHIEvent[]>(() => {
    const managed = (managedEventsQuery.data ?? []) as CHIEvent[];
    const managedSlugs = new Set(managed.map(event => event.slug));
    const managedWithCuratedArtwork = managed.map(event => {
      const catalogEvent = CANONICAL_EVENTS.find(candidate => candidate.slug === event.slug);
      return catalogEvent?.eventImage && !event.eventImage
        ? { ...event, eventImage: catalogEvent.eventImage }
        : event;
    });
    return [...CANONICAL_EVENTS.filter(event => !managedSlugs.has(event.slug)), ...managedWithCuratedArtwork];
  }, [managedEventsQuery.data]);
  const eventGraphicPollingStartedAt = useRef(Date.now());
  const eventGraphicsQuery = trpc.events.publicGraphics.useQuery(undefined, {
    retry: false,
    /*
     * Generation runs asynchronously. Poll only while this visible page is young
     * and still missing artwork; this lets freshly generated art appear without
     * turning an open browser tab into an indefinite source of database traffic.
     */
    refetchInterval: query => {
      const readyCount = query.state.data?.length ?? 0;
      const withinAutomaticWindow = Date.now() - eventGraphicPollingStartedAt.current < 10 * 60_000;
      return readyCount < EVENTS.length && withinAutomaticWindow ? 15_000 : false;
    },
    refetchIntervalInBackground: false,
  });
  const eventGraphicBySlug = useMemo(
    () => new Map((eventGraphicsQuery.data ?? []).map(graphic => [graphic.eventSlug, graphic.url])),
    [eventGraphicsQuery.data],
  );
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  /** Reveals the share pill's label while it is hovered or keyboard-focused. */
  const [copyPillHovered, setCopyPillHovered] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<CalendarEventTypeFilter>(DEFAULT_CALENDAR_EVENT_FILTERS.type);
  const [filterLocation, setFilterLocation] = useState(DEFAULT_CALENDAR_EVENT_FILTERS.location);
  const [filterSpeaker, setFilterSpeaker] = useState(DEFAULT_CALENDAR_EVENT_FILTERS.speaker);
  const [filterCost, setFilterCost] = useState<CalendarCostFilter>(DEFAULT_CALENDAR_EVENT_FILTERS.cost);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  /** Free-text query across title, partner, speaker, organisation, and location. */
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState<string>(ALL_MONTHS);
  /** Topic narrowing, held apart from `filterType` so a format and a topic compose. */
  const [filterCategory, setFilterCategory] = useState<string>("all");
  /**
   * Whether the listing shows what has already happened instead of what is coming.
   *
   * A separate flag rather than another value of the category filter, because past
   * events are a different question from what kind of event it was — someone looking
   * back still wants to narrow by format and topic while they do it.
   */
  const [showPast, setShowPast] = useState(false);

  const [shareOpenId, setShareOpenId] = useState<number | null>(null);
  const [copiedEventId, setCopiedEventId] = useState<number | null>(null);
  const [hoveredEventId, setHoveredEventId] = useState<number | null>(null);
  const checkoutWindowRef = useRef<ReservedCheckoutWindow | null>(null);
  const selectedEventArt = useMemo(
    () => {
      if (!selectedEvent) return null;
      return eventFeaturedArtwork({
        slug: selectedEvent.slug,
        title: selectedEvent.title,
        eventImage: selectedEvent.eventImage,
        heroImage: selectedEvent.heroImage,
        generatedImage: eventGraphicBySlug.get(selectedEvent.slug),
      }, { width: 1400, height: 440 });
    },
    [eventGraphicBySlug, selectedEvent],
  );
  useEffect(
    () => () => closeReservedCheckoutWindow(checkoutWindowRef.current),
    [],
  );

  const locationOptions = getCalendarLocationOptions(EVENTS);
  const speakerOptions = getCalendarSpeakerOptions(EVENTS);
  const activeFilters = {
    type: filterType,
    location: filterLocation,
    speaker: filterSpeaker,
    cost: filterCost,
  } as const;
  const hasActiveFilters = hasActiveCalendarEventFilters(activeFilters)
    || search.trim() !== ""
    || filterMonth !== ALL_MONTHS
    || filterCategory !== "all";

  /** Shared live clock moves an ended event without requiring a page refresh. */
  const eventClock = useEventClock();
  const monthOptions = useMemo(() => getCalendarMonthOptions(EVENTS), [EVENTS]);

  /*
   * Upcoming reads forward in time so the next event to attend is first. Past reads
   * backward, most recent first — looking back, the thing that just happened is the
   * one being looked for.
   */
  const filteredEvents = useMemo(() => {
    const narrowed = filterCalendarEvents(EVENTS, activeFilters).filter(event => (
      isPastCalendarEvent(event, eventClock) === showPast
      && matchesCalendarSearch(event, search)
      && matchesCalendarMonth(event, filterMonth)
      && (filterCategory === "all" || event.category === filterCategory)
    ));

    return narrowed.sort((left, right) => {
      const byDate = left.date.localeCompare(right.date) || left.time.localeCompare(right.time);
      return showPast ? -byDate : byDate;
    });
  }, [EVENTS, activeFilters, eventClock, showPast, search, filterMonth, filterCategory]);

  /*
   * The hero's event: the soonest one still to come, or — when nothing is scheduled
   * — the most recent one that has passed, so the section is never empty while the
   * calendar is between cycles.
   */
  const { featuredEvent, featuredIsPast } = useMemo(() => {
    const chronological = [...EVENTS].sort(
      (left, right) => left.date.localeCompare(right.date) || left.time.localeCompare(right.time),
    );
    const upcoming = chronological.find(event => !isPastCalendarEvent(event, eventClock));
    if (upcoming) return { featuredEvent: upcoming, featuredIsPast: false };

    const past = chronological.filter(event => isPastCalendarEvent(event, eventClock)).pop();
    return { featuredEvent: past ?? null, featuredIsPast: Boolean(past) };
  }, [EVENTS, eventClock]);
  const featuredEventArt = useMemo(
    () => {
      if (!featuredEvent) return null;
      return eventFeaturedArtwork({
        slug: featuredEvent.slug,
        title: featuredEvent.title,
        eventImage: featuredEvent.eventImage,
        heroImage: featuredEvent.heroImage,
        generatedImage: eventGraphicBySlug.get(featuredEvent.slug),
      }, { width: 1200, height: 675 });
    },
    [eventGraphicBySlug, featuredEvent],
  );

  /**
   * The partner named in the featured event's subtitle.
   *
   * Subtitles are written as "In Partnership with X", so the prefix is stripped to
   * leave the name the card should show. A subtitle in any other shape is not a
   * partnership and yields nothing rather than a mislabelled card.
   */
  const featuredPartner = useMemo(() => {
    const match = featuredEvent?.subtitle?.match(/^in partnership with\s+(.+)$/i);
    return match?.[1]?.trim() ?? null;
  }, [featuredEvent]);
  const { featuredPanel, featuredPanelOverflow } = useMemo(() => {
    if (!featuredEvent) return { featuredPanel: [], featuredPanelOverflow: 0 };
    const people = publishedSpeakers
      .filter(speaker => speaker.eventSlug === featuredEvent.slug)
      .map(speaker => ({
        name: speaker.name,
        portrait: resolveSpeakerPortrait({ eventSlug: featuredEvent.slug, speakerName: speaker.name, publishedSpeakers }),
      }));
    const resolved = people.length > 0 ? people : [{
      name: featuredEvent.speaker,
      portrait: resolveSpeakerPortrait({
        eventSlug: featuredEvent.slug, speakerName: featuredEvent.speaker,
        heroImage: featuredEvent.heroImage, publishedSpeakers,
      }),
    }];
    return { featuredPanel: resolved.slice(0, 3), featuredPanelOverflow: Math.max(0, resolved.length - 3) };
  }, [featuredEvent, publishedSpeakers]);

  /*
   * The tab strip. Formats and topics both come from the data rather than a fixed
   * list, so a tab never offers a filter that matches nothing.
   */
  const eventTabs = useMemo(() => {
    /*
     * Blank categories are filtered out. A managed event created without one yields
     * an empty string, which rendered as a nameless tab that filtered the list to
     * nothing — clickable, unlabelled, and impossible to recover from except by
     * choosing another tab.
     */
    const categories = Array.from(new Set(EVENTS.map(event => event.category?.trim()).filter(Boolean)))
      .sort((left, right) => left.localeCompare(right));

    return [
      { id: "all", label: "All Events" },
      { id: "in-person", label: "In-Person" },
      { id: "virtual", label: "Virtual" },
      ...categories.map(category => ({ id: `category:${category}`, label: category })),
      { id: "past", label: "Past Events" },
    ];

  }, [EVENTS]);

  /*
   * Derived rather than stored, so the tab strip cannot fall out of step with the
   * dropdowns below it — changing Type in the disclosure moves the tab, and vice
   * versa, because both read and write the same state.
   */
  const activeTab = showPast
    ? "past"
    : filterCategory !== "all"
      ? `category:${filterCategory}`
      : filterType !== "all"
        ? filterType
        : "all";

  const selectTab = (id: string) => {
    if (id === "past") {
      setShowPast(true);
      return;
    }
    setShowPast(false);

    if (id === "all") {
      setFilterType("all");
      setFilterCategory("all");
      return;
    }
    if (id.startsWith("category:")) {
      setFilterCategory(id.slice("category:".length));
      setFilterType("all");
      return;
    }
    setFilterType(id as CalendarEventTypeFilter);
    setFilterCategory("all");
  };
  const myRegistrationsQuery = trpc.events.myRegistrations.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
  });
  const selectedPricingPolicyQuery = trpc.events.publicPricingPolicy.useQuery(
    { eventSlug: selectedEvent?.slug ?? "" },
    { enabled: Boolean(selectedEvent), retry: false },
  );
  const registeredEventSlugs = new Set(
    (myRegistrationsQuery.data ?? [])
      .filter(registration => registration.registrationState === "registered")
      .map(registration => registration.eventSlug),
  );
  const attendeeZoomLinksBySlug = new Map(
    (myRegistrationsQuery.data ?? [])
      .filter(registration => registration.registrationState === "registered" && Boolean(registration.joinUrl))
      .map(registration => [registration.eventSlug, registration.joinUrl] as const),
  );
  const registeredIds = EVENTS.filter(event => registeredEventSlugs.has(event.slug)).map(event => event.id);
  /*
   * CHI-22: Buy Tickets replaces every register / sign-in / join / checkout CTA
   * on internally Stripe-paid events for any viewer without a blocking ticket.
   * Special events are skipped (the helper is false for them).
   *
   * Ticket data (signed in only; signed-out viewers make no ticket call):
   *  - Quick Look / register handler (one event): Dev3's useMyTicketsForEvent, which
   *    carries Dev2's hasBlockingTicket (active OR suspended).
   *  - Featured + cards (many events): Dev3's useMyTicketsBySlug, ONE events.myTickets
   *    call grouped by slug (no N+1, no hooks in loops). hasBlockingTicket per slug comes
   *    from Dev2's blockingEventSlugs (active OR suspended, so a disputed ticket hides Buy
   *    Tickets on cards too); without the field it falls back to active-ticket presence.
   */
  const myTicketsBySlug = useMyTicketsBySlug(isAuthenticated);
  const selectedEventTickets = useMyTicketsForEvent(selectedEvent?.slug ?? "", isAuthenticated && Boolean(selectedEvent));
  const ctaClock = new Date(useServerNow(selectedEventTickets.serverNow ?? myTicketsBySlug.serverNow, 30_000));
  const hasBlockingTicketFor = (slug: string) =>
    selectedEvent?.slug === slug && selectedEventTickets.status === "ok"
      ? selectedEventTickets.hasBlockingTicket
      : myTicketsBySlug.hasBlockingTicket(slug);
  const ctaPricingModes = useEventPricingModes(EVENTS.filter(event => !event.isSpecialEvent).map(event => event.slug));
  const ctaCheckoutEnabled = useTicketsCheckoutEnabled(EVENTS.filter(event => !event.isSpecialEvent).map(event => event.slug))
    || ticketsCheckoutEnabled(selectedPricingPolicyQuery.data);
  const buyTicketsFor = (event: CHIEvent) => shouldShowBuyTickets(
    {
      slug: event.slug,
      pricingMode: ctaPricingModes.get(event.slug) ?? (selectedEvent?.slug === event.slug ? selectedPricingPolicyQuery.data?.pricingMode : null),
      isSpecialEvent: event.isSpecialEvent === true,
      hasEnded: isPastCalendarEvent(event, eventClock) || isPastCalendarEvent(event, ctaClock),
      ticketsCheckoutEnabled: ctaCheckoutEnabled,
    },
    { hasBlockingTicket: hasBlockingTicketFor(event.slug) },
  );
  const eventBySlug = new Map(EVENTS.map(event => [event.slug, event]));
  /** Pending review is special-only. Waitlisted is disabled only on approval-queue events. */
  const awaitingDecisionBySlug = new Map(
    (myRegistrationsQuery.data ?? []).flatMap(registration => {
      const status = approvalStatusFromRegistrationState(registration.registrationState);
      const gated =
        status === "pending" ||
        (status === "waitlisted" && isApprovalQueueEvent(eventBySlug.get(registration.eventSlug)));
      return gated && status ? [[registration.eventSlug, status] as const] : [];
    }),
  );
  const canReapplyBySlug = new Set(
    (myRegistrationsQuery.data ?? [])
      .filter(registration => registration.canReapply)
      .map(registration => registration.eventSlug),
  );
  const selectedAwaitingDecision = selectedEvent ? awaitingDecisionBySlug.get(selectedEvent.slug) : undefined;
  const selectedCanReapply = Boolean(selectedEvent && canReapplyBySlug.has(selectedEvent.slug));
  const modalAwaitingDecision = Boolean(selectedAwaitingDecision) && !selectedCanReapply && !(selectedEvent && isPastCalendarEvent(selectedEvent, eventClock));
  const selectedAttendeeZoomLink = selectedEvent ? attendeeZoomLinksBySlug.get(selectedEvent.slug) : undefined;

  const clearFilters = () => {
    const resetFilters = resetCalendarEventFilters();
    setFilterType(resetFilters.type);
    setFilterLocation(resetFilters.location);
    setFilterSpeaker(resetFilters.speaker);
    setFilterCost(resetFilters.cost);
    setSearch("");
    setFilterMonth(ALL_MONTHS);
    setFilterCategory("all");
  };

  /**
   * Everything the empty state offers to undo, including the upcoming/past choice.
   *
   * Distinct from clearFilters, which the disclosure's Reset uses: someone who has
   * deliberately switched to Past Events and then over-narrowed wants their filters
   * cleared, not to be thrown back to the upcoming list. The empty state is the one
   * place where returning to the default view is the useful escape.
   */
  const clearAllNarrowing = () => {
    clearFilters();
    setShowPast(false);
  };

  const eventUrl = (event: Event) => `${window.location.origin}/events/${event.slug}`;

  function fallbackCopy(text: string) {
    const input = document.createElement("textarea");
    input.value = text;
    input.setAttribute("readonly", "");
    input.style.position = "fixed";
    input.style.opacity = "0";
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
  }

  function copyEventUrl(event: Event) {
    const url = eventUrl(event);
    const confirmCopy = () => {
      setCopiedEventId(event.id);
      chiNotify.success("Event link copied", {
        description: "The event page link is ready to share.",
        dedupeKey: `calendar-event-link-${event.slug}`,
      });
      window.setTimeout(() => setCopiedEventId(current => current === event.id ? null : current), 1800);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(confirmCopy).catch(() => {
        fallbackCopy(url);
        confirmCopy();
      });
    } else {
      fallbackCopy(url);
      confirmCopy();
    }
  }

  function shareEvent(network: "facebook" | "linkedin", event: Event) {
    const url = encodeURIComponent(eventUrl(event));
    const destination = network === "facebook"
      ? `https://www.facebook.com/sharer/sharer.php?u=${url}`
      : `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    window.open(destination, "_blank", "noopener,noreferrer");
    setShareOpenId(null);
  }

  function openEvent(event: Event) {
    setRegistrationError(null);
    setSelectedEvent(event);
    setModal("event");
  }

  function handlePastEventRecording(event: Event) {
    setSelectedEvent(event);

    if (!hasPaidAccess) {
      setModal("recordingGate");
      return;
    }

    /*
     * Recordings do not yet have their own URL in the event model. Keep the
     * member on the page rather than guessing a destination, treating an
     * expired meeting link as a recording, or exposing an organizer's raw Zoom
     * URL from public event data.
     */
    chiNotify.info("Highlight reel coming soon", {
      description: `${event.title} is included with your member access. Its recording will appear here when it is ready.`,
      dedupeKey: `calendar-highlight-reel-${event.slug}`,
    });
  }

  function completeRegistration(result: {
    status: "registered" | "already_registered" | "pending_review" | "external_checkout_required" | "unavailable";
    checkoutUrl?: string;
    reason?: string;
  }) {
    if (result.status === "external_checkout_required" && result.checkoutUrl) {
      chiNotify.info("Continue to secure registration", {
        description: "Opening the secure event checkout in a new tab.",
        dedupeKey: `calendar-checkout-${selectedEvent?.slug ?? "event"}`,
      });
      const reservedWindow = checkoutWindowRef.current;
      checkoutWindowRef.current = null;
      handoffToCheckout(result.checkoutUrl, reservedWindow);
      return;
    }
    closeReservedCheckoutWindow(checkoutWindowRef.current);
    checkoutWindowRef.current = null;
    if (result.status === "unavailable") {
      const message = result.reason ?? "Registration is not currently available for this event.";
      setRegistrationError(message);
      chiNotify.warning("Registration is unavailable", {
        description: message,
        dedupeKey: `calendar-unavailable-${selectedEvent?.slug ?? "event"}`,
      });
      void trpcUtils.events.myRegistrations.invalidate();
      setModal("event");
      return;
    }
    if (result.status === "pending_review") {
      void trpcUtils.events.myRegistrations.invalidate();
      chiNotify.info("Registration received", {
        description: "Your registration is under review. We’ll let you know as soon as it’s confirmed.",
        dedupeKey: `calendar-registration-review-${selectedEvent?.slug ?? "event"}`,
      });
      setModal("event");
      return;
    }
    void Promise.all([
      trpcUtils.events.myRegistrations.invalidate(),
      trpcUtils.events.myZoomJoinLink.invalidate({ eventSlug: selectedEvent?.slug ?? "" }),
    ]);
    setRegistrationError(null);
    chiNotify.success(
      result.status === "already_registered" ? "You’re already registered" : "Registration confirmed",
      {
        description: `${selectedEvent?.title ?? "This event"} is saved in My Events.`,
        dedupeKey: `calendar-registration-${selectedEvent?.slug ?? "event"}`,
      },
    );
    setModal("confirmed");
  }

  const reapplyMutation = trpc.events.reapply.useMutation({
    onSuccess: completeRegistration,
    onError: error => {
      const message = error.message || "We could not submit your registration. Please try again.";
      setRegistrationError(message);
      chiNotify.error("Registration could not be submitted", {
        description: message,
        dedupeKey: `calendar-reapply-error-${selectedEvent?.slug ?? "event"}`,
      });
      setModal("event");
    },
  });
  const registrationMutation = trpc.events.register.useMutation({
    onSuccess: completeRegistration,
    onError: error => {
      closeReservedCheckoutWindow(checkoutWindowRef.current);
      checkoutWindowRef.current = null;
      const presented = presentRegisterFailure(error, () => {
        void trpcUtils.events.myRegistrations.invalidate();
      });
      if (presented.kind === "blocked") {
        setRegistrationError(null);
        chiNotify.info(presented.message, { dedupeKey: `calendar-registration-${selectedEvent?.slug ?? "event"}` });
        setModal("event");
        return;
      }
      setRegistrationError(presented.message);
      chiNotify.error("Registration could not be completed", {
        description: presented.message,
        dedupeKey: `calendar-registration-error-${selectedEvent?.slug ?? "event"}`,
      });
      setModal("event");
    },
  });

  function handleRegisterClick(eventToRegister: Event | null = selectedEvent) {
    if (!eventToRegister) return;
    // CHI-22: any remaining register path for a Buy Tickets event goes to the tickets page instead.
    if (buyTicketsFor(eventToRegister)) {
      navigate(ticketsHref(eventToRegister.slug));
      return;
    }
    const awaiting = awaitingDecisionBySlug.get(eventToRegister.slug);
    const canReapply = canReapplyBySlug.has(eventToRegister.slug);
    if (awaiting && !canReapply && !isPastCalendarEvent(eventToRegister, eventClock)) {
      chiNotify.info(REGISTRANT_BLOCK_MESSAGES[awaiting], { dedupeKey: `calendar-registration-${eventToRegister.slug}` });
      return;
    }
    setRegistrationError(null);
    // G4.1: a special (approval-queue) event never leaves for an external link or checkout.
    const isSpecialEvent = eventToRegister.isSpecialEvent === true;
    const externalRegistrationUrl = isSpecialEvent ? undefined : getPostRegistrationRedirectUrl(eventToRegister);
    const attendeeZoomLink = attendeeZoomLinksBySlug.get(eventToRegister.slug);
    const action = resolveEventRegistrationAction({
      isAuthenticated,
      pricingPolicy: selectedPricingPolicyQuery.data,
      externalRegistrationUrl,
      joinUrl: attendeeZoomLink ?? undefined,
      hasEnded: isPastCalendarEvent(eventToRegister, eventClock),
      registrationState: myRegistrationsQuery.data?.find(registration => registration.eventSlug === eventToRegister.slug)?.registrationState,
      isSpecialEvent,
    });

    /*
     * A finished event has a recording to offer, not a seat to reserve. Every
     * CTA on this page already checks that before calling in, so this is the
     * backstop for a path that forgets to — it sends the same place they do
     * rather than falling through to a registration the event cannot honour.
     */
    if (action === "has_ended") {
      handlePastEventRecording(eventToRegister);
      return;
    }

    if (action === "sign_in") {
      // Offer the choice rather than assuming it.
      //
      // This navigated straight to /login, which is the wrong guess for the
      // person most likely to be here: someone who found an event, wants in, and
      // has no account yet. They landed on a sign-in form with no obvious way
      // forward. The modal presents both routes and carries the event through
      // either one.
      //
      // The modal already existed and nothing ever opened it, so this is the
      // branch it was written for.
      setSelectedEvent(eventToRegister);
      setModal("signin");
      return;
    }
    if (action === "unavailable") {
      const message = "Registration is not currently available for this event.";
      setRegistrationError(message);
      chiNotify.warning("Registration is unavailable", {
        description: message,
        dedupeKey: `calendar-unavailable-${eventToRegister.slug}`,
      });
      setModal("event");
      return;
    }
    if (action === "pending_review") {
      chiNotify.info(PENDING_REVIEW_MESSAGE, {
        description: `${eventToRegister.title} is reviewed by the organizers before your place is confirmed.`,
        dedupeKey: `calendar-registration-${eventToRegister.slug}`,
      });
      return;
    }
    // CHI-20: internally Stripe-paid, non-special events buy tickets on their own page.
    if (action === "register" && !canReapply && shouldUseTicketsPage({ pricingPolicy: selectedPricingPolicyQuery.data, isSpecialEvent: eventToRegister.isSpecialEvent === true })) {
      navigate(getEventTicketsUrl(eventToRegister.slug));
      return;
    }
    if (myRegistrationsQuery.data?.find(registration => registration.eventSlug === eventToRegister.slug)?.canReapply) {
      reapplyMutation.mutate({ eventSlug: eventToRegister.slug });
      return;
    }
    if (action === "external_checkout" || action === "register") {
      const expectsCheckout = !isSpecialEvent && (
        action === "external_checkout" ||
        eventToRegister.registrationMode === "paid" ||
        selectedPricingPolicyQuery.data?.pricingMode === "paid_internal");
      checkoutWindowRef.current = expectsCheckout ? reserveCheckoutWindow() : null;
      registrationMutation.mutate({ eventSlug: eventToRegister.slug });
      return;
    }
    if (action === "external_registration" && externalRegistrationUrl) {
      window.location.assign(externalRegistrationUrl);
      return;
    }
    if (action === "join" && attendeeZoomLink) {
      window.open(attendeeZoomLink, "_blank", "noopener,noreferrer");
      return;
    }
  }

  function handleConfirmRegister() {
    handleRegisterClick();
  }

  function closeAll() {
    setModal(null);
    setSelectedEvent(null);
  }

  const myEvents = EVENTS.filter(e => registeredIds.includes(e.id));

  /*
    The page backdrop. An earlier pass left backgroundAttachment/backgroundSize
    on this container with no image to act on, so they did nothing; the block
    below completes that intent rather than adding a second layer on top of it.

    The scrim is baked into the same backgroundImage as the first gradient layer
    instead of living on a separate overlay element, so there is exactly one
    background on this page and no way for the two to drift apart. It is a flat
    92% wash of the site's own #080C18 rather than a soft fade, because the copy
    runs the full height of a long page — a directional fade would only protect
    whichever end it was tuned for. At 92% the photograph reads as texture
    behind the surfaces and body copy keeps its dark-theme contrast unchanged.

    backgroundAttachment is fixed so cover is measured against the viewport.
    With the default scroll value cover would size the image to the whole
    scrolling document, which on a page this tall blows the frame up and crops
    away nearly all of it — the opposite of showing as much of the image as
    possible. backgroundColor stays as the paint before the image loads.
  */
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#080C18",
        backgroundImage:
          "linear-gradient(rgba(8,12,24,0.92), rgba(8,12,24,0.92)), url('/media/backgrounds/calendar-hero.webp')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Navigation />

      {/* ── FEATURED EVENT HERO ── */}
      <section className="events-hero">
        {/*
          The stage photograph and its scrims. Kept as layers behind the copy rather
          than a single background so the left-to-right fade can be tuned
          independently of the image, which is what keeps the headline legible over
          a busy photo at every width.
        */}
        <FeaturedEventBackground
          className="events-hero__stage"
          imageSrc={featuredEventArt?.url}
        />
        <div className="events-hero__scrim" aria-hidden="true" />

        <div className="container events-hero__inner">
          <div className="events-hero__copy">
            <SectionBadge className="events-hero__eyebrow" variant="gradient">
              {featuredIsPast ? "Most Recent CHI Experience" : "Next CHI Experience"}
            </SectionBadge>

            {featuredEvent ? (
              <>
                <h1 className="events-hero__title">{featuredEvent.title}</h1>
                {featuredEvent.subtitle && (
                  <p className="events-hero__partner">{featuredEvent.subtitle}</p>
                )}
                <p className="events-hero__lede">{featuredEvent.description}</p>

                <div className="events-hero__meta">
                  <span><Calendar size={17} aria-hidden="true" /> {featuredEvent.displayDate}</span>
                  <span><Clock size={17} aria-hidden="true" /> {featuredEvent.time}</span>
                  <span><MapPin size={17} aria-hidden="true" /> {featuredEvent.location}</span>
                </div>

                <div className="events-hero__actions">
                  {buyTicketsFor(featuredEvent) ? (
                    <BuyTicketsButton slug={featuredEvent.slug} className="events-button events-button--gold" />
                  ) : (
                  <button
                    type="button"
                    className="events-button events-button--gold"
                    onClick={() => {
                      if (featuredIsPast) {
                        handlePastEventRecording(featuredEvent);
                        return;
                      }
                      const joinUrl = attendeeZoomLinksBySlug.get(featuredEvent.slug);
                      if (joinUrl) {
                        window.open(joinUrl, "_blank", "noopener,noreferrer");
                        return;
                      }
                      openEvent(featuredEvent);
                      if (!registeredEventSlugs.has(featuredEvent.slug)) handleRegisterClick(featuredEvent);
                    }}
                  >
                    {featuredIsPast
                      ? hasPaidAccess
                        ? <><PlayCircle size={16} aria-hidden="true" /> Watch the highlight reel!</>
                        : <><LockKeyhole size={16} aria-hidden="true" /> Past Event - Watch a recording</>
                      : attendeeZoomLinksBySlug.get(featuredEvent.slug)
                      ? <><ExternalLink size={16} aria-hidden="true" /> Join on Zoom</>
                      : registeredEventSlugs.has(featuredEvent.slug)
                        ? <><CheckCircle size={16} aria-hidden="true" /> You’re Registered</>
                        : <><Ticket size={16} aria-hidden="true" /> Reserve Your Seat</>}
                  </button>
                  )}
                  <button
                    type="button"
                    className="events-button events-button--ghost"
                    onClick={() => openEvent(featuredEvent)}
                  >
                    View Event Details <ArrowRight size={15} aria-hidden="true" />
                  </button>
                </div>

                {featuredEvent.speaker?.trim() && (
                  <div className="events-hero__panel">
                    <p className="events-hero__panel-label">{featuredEvent.speaker}</p>
                    <div className="events-hero__panel-row">
                      <div className="events-hero__avatars">
                        {featuredPanel.map(person => (
                          <SpeakerAvatar
                            key={person.name}
                            name={person.name}
                            portraitUrl={person.portrait}
                            fallbackClassName="events-hero__avatar-initials"
                          />
                        ))}
                        {featuredPanelOverflow > 0 && (
                          <span className="events-hero__avatar-initials events-hero__avatar-more" aria-hidden="true">
                            +{featuredPanelOverflow}
                          </span>
                        )}
                      </div>
                      <span className="events-hero__panel-note">
                        with CHI Alliance leaders and special guests
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <h1 className="events-hero__title">The next gathering is being scheduled</h1>
                <p className="events-hero__lede">
                  New roundtables, panels, and networking events are added throughout the year.
                  Create your profile and we will let you know the moment the next one opens.
                </p>
                <div className="events-hero__actions">
                  <button
                    type="button"
                    className="events-button events-button--gold"
                    onClick={() => navigate("/create-account?source=calendar-hero")}
                  >
                    Create Your Account <ArrowRight size={15} aria-hidden="true" />
                  </button>
                </div>
              </>
            )}
          </div>

          {/*
            The partner card sits over the photograph, and only when the featured
            event actually names a partner in its subtitle — an empty card floating
            on the image would read as a loading failure.
          */}
          {featuredPartner && (
            <aside className="events-hero__partner-card">
              <small>In partnership with</small>
              <strong>{featuredPartner}</strong>
            </aside>
          )}
        </div>
      </section>

      {/* ── FILTER BAR ── */}
      <div className="container events-filterbar-wrap">
        <div className="events-filterbar">
          <div className="events-tabs" role="tablist" aria-label="Event categories">
            {eventTabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className="events-tab"
                data-active={activeTab === tab.id || undefined}
                onClick={() => selectTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <label className="events-search">
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search events…"
              aria-label="Search events"
            />
          </label>

          <label className="events-month">
            <span className="sr-only">Filter by month</span>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="calendar-filter-trigger" aria-label="Filter events by month">
                <SelectValue placeholder="All months" />
              </SelectTrigger>
              <SelectContent className="calendar-filter-content">
                <SelectItem value={ALL_MONTHS}>All months</SelectItem>
                {monthOptions.map(month => (
                  <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          {/* The remaining filters live behind this rather than widening the bar:
              location, speaker, and cost are refinements, not the primary axis. */}
          <button
            type="button"
            className="events-filter-toggle"
            data-active={filtersExpanded || hasActiveFilters || undefined}
            aria-expanded={filtersExpanded}
            aria-controls="calendar-event-filter-controls"
            onClick={() => setFiltersExpanded(expanded => !expanded)}
          >
            <Filter size={15} aria-hidden="true" />
            <span>Find an event</span>
            <ChevronDown className="calendar-filter-heading__chevron" size={15} aria-hidden="true" />
          </button>
        </div>

        {filtersExpanded && (
          <div id="calendar-event-filter-controls" className="events-filter-disclosure">
            <div className="calendar-filter-grid">
              <label className="calendar-filter-field">
                <span>Type</span>
                <Select value={filterType} onValueChange={(value) => setFilterType(value as CalendarEventTypeFilter)}>
                  <SelectTrigger className="calendar-filter-trigger" aria-label="Filter events by type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent className="calendar-filter-content">
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="virtual"><Video size={14} aria-hidden="true" />Virtual</SelectItem>
                    <SelectItem value="in-person"><Building2 size={14} aria-hidden="true" />In-person</SelectItem>
                  </SelectContent>
                </Select>
              </label>

              <label className="calendar-filter-field">
                <span>Location</span>
                <Select value={filterLocation} onValueChange={setFilterLocation}>
                  <SelectTrigger className="calendar-filter-trigger" aria-label="Filter events by location">
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent className="calendar-filter-content">
                    <SelectItem value="all">All locations</SelectItem>
                    {locationOptions.map((location) => (
                      <SelectItem key={location} value={location}><MapPin size={14} aria-hidden="true" />{location}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>

              <label className="calendar-filter-field">
                <span>Speaker</span>
                <Select value={filterSpeaker} onValueChange={setFilterSpeaker}>
                  <SelectTrigger className="calendar-filter-trigger" aria-label="Filter events by speaker">
                    <SelectValue placeholder="All speakers" />
                  </SelectTrigger>
                  <SelectContent className="calendar-filter-content">
                    <SelectItem value="all">All speakers</SelectItem>
                    {speakerOptions.map((event) => (
                      <SelectItem key={event.speaker} value={event.speaker}>
                        {event.speaker}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>

              <label className="calendar-filter-field">
                <span>Cost</span>
                <Select value={filterCost} onValueChange={(value) => setFilterCost(value as CalendarCostFilter)}>
                  <SelectTrigger className="calendar-filter-trigger" aria-label="Filter events by registration cost">
                    <SelectValue placeholder="All pricing" />
                  </SelectTrigger>
                  <SelectContent className="calendar-filter-content">
                    <SelectItem value="all">All pricing</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </label>
            </div>
            <div className="calendar-filter-summary">
              <p id="calendar-filter-results" className="calendar-filter-results" aria-live="polite">
                Showing {filteredEvents.length} {filteredEvents.length === 1 ? "event" : "events"}{hasActiveFilters ? " matching your filters" : ""}.
              </p>
              <button
                type="button"
                className="calendar-filter-reset"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                <RotateCcw size={14} aria-hidden="true" />
                Reset
              </button>
            </div>
          </div>
        )}
      </div>

      {/*
        ── EVENT GRID ──

        Named so the schedule can be addressed on its own. The hero always shows the
        next event regardless of the filters — it is the page's headline, not a
        result — so "is this event in the schedule?" is only answerable inside this
        region. Tests rely on that distinction, and so does a screen-reader user
        skipping to the listing.
      */}
      <section className="events-listing" aria-label="Event schedule">
        <div className="container">
          <div className="events-section-heading">
            <h2>{showPast ? "Past Events" : "Upcoming Events"}</h2>
            <p>
              {showPast
                ? "Conversations the community has already had. Details stay available for anyone catching up."
                : "Curated conversations designed to connect leaders, share practical insight, and create meaningful professional relationships."}
            </p>
          </div>

          {filteredEvents.length === 0 ? (
            <div className="calendar-no-results" role="status">
              <p>
                {showPast
                  ? "No past events match all of the filters you selected."
                  : "No events match all of the filters you selected."}
              </p>
              <button type="button" className="calendar-inline-reset" onClick={clearAllNarrowing}>
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="events-grid">
              {filteredEvents.map(event => {
                const isRegistered = registeredIds.includes(event.id);
                const attendeeZoomLink = attendeeZoomLinksBySlug.get(event.slug);
                const eventIsPast = isPastCalendarEvent(event, eventClock);
                const showRegisteredStatus = !eventIsPast && isRegistered && !attendeeZoomLink;
                const cardAwaitingDecision = eventIsPast ? undefined : awaitingDecisionBySlug.get(event.slug);
                const cardCanReapply = canReapplyBySlug.has(event.slug);
                const isDateToBeAnnounced = event.displayDate.includes("TBD") || event.time === "TBD";
                const assignedSpeakers = publishedSpeakers.filter(speaker => speaker.eventSlug === event.slug);
                const eventTileSpeakers: PublishedSpeakerPortrait[] = assignedSpeakers.length > 0
                  ? assignedSpeakers
                  : [{
                      eventSlug: event.slug,
                      name: event.speaker,
                      portraitUrl: resolveSpeakerPortrait({
                        eventSlug: event.slug,
                        speakerName: event.speaker,
                        heroImage: event.heroImage,
                        publishedSpeakers,
                      }),
                    }];
                const eventArtwork = eventFeaturedArtwork({
                  slug: event.slug,
                  title: event.title,
                  eventImage: event.eventImage,
                  heroImage: event.heroImage,
                  generatedImage: eventGraphicBySlug.get(event.slug),
                }, { width: 1200, height: 675 });
                const showShareTrigger = hoveredEventId === event.id || shareOpenId === event.id;
                const [monthLabel, dayLabel] = event.displayDate.split(" ");

                return (
                  <article
                    key={event.id}
                    className={`events-card${isRegistered ? " events-card--registered" : ""}`}
                    style={{ "--event-accent": event.color } as React.CSSProperties}
                    /*
                      The card is the event's page. Kept as an onClick on a
                      non-anchor container rather than wrapping the card in a link:
                      the register and share controls inside it are real buttons,
                      and nesting them in an anchor would put them out of reach of
                      a keyboard. Each of those stops propagation for itself.
                    */
                    onClick={() => navigate(`/events/${event.slug}`)}
                    onMouseEnter={() => setHoveredEventId(event.id)}
                    onMouseLeave={() => { if (shareOpenId !== event.id) setHoveredEventId(null); }}
                    onFocusCapture={() => setHoveredEventId(event.id)}
                    onBlurCapture={e => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node) && shareOpenId !== event.id) {
                        setHoveredEventId(null);
                      }
                    }}
                  >
                    <div className="events-card__top">
                      <span className="events-card__format">
                        {event.type === "virtual" ? <Video size={11} aria-hidden="true" /> : <Building2 size={11} aria-hidden="true" />}
                        {event.type === "virtual" ? "Virtual" : "In-Person"}
                      </span>
                      {isRegistered && (
                        <span className="events-card__registered">
                          <CheckCircle size={11} aria-hidden="true" /> Registered
                        </span>
                      )}
                    </div>

                    <ResponsiveImage
                      className="events-card__art"
                      aria-hidden="true"
                      src={eventArtwork.url}
                      alt=""
                      loading="eager"
                      maxWidth={640}
                      sizes="(max-width: 700px) 100vw, 33vw"
                    />

                    <div className="events-card__headline">
                      <div className="events-card__date" aria-hidden={isDateToBeAnnounced ? undefined : "true"}>
                        {isDateToBeAnnounced ? (
                          <>
                            <CalendarClock size={17} aria-hidden="true" />
                            <span className="events-card__date-tba">Date TBA</span>
                          </>
                        ) : (
                          <>
                            <small>{monthLabel}</small>
                            <strong>{dayLabel?.replace(",", "") ?? ""}</strong>
                          </>
                        )}
                      </div>
                      <div className="events-card__titles">
                        <h3>{event.title}</h3>
                        {event.subtitle && <p className="events-card__subtitle">{event.subtitle}</p>}
                      </div>
                    </div>

                    <div className="events-card__meta">
                      <span><Clock size={13} aria-hidden="true" /> {event.time}</span>
                      <span>
                        {event.type === "virtual" ? <Video size={13} aria-hidden="true" /> : <MapPin size={13} aria-hidden="true" />}
                        {event.location}
                      </span>
                      {/* Omitted rather than rendered empty: a managed event created
                          without a category otherwise showed a lone icon with no
                          label beside it. */}
                      {event.category?.trim() && (
                        <span><Star size={13} aria-hidden="true" /> {event.category}</span>
                      )}
                    </div>

                    <div className="events-card__footer">
                      <div className="events-card__speaker">
                        <RotatingEventTileSpeaker
                          speakers={eventTileSpeakers}
                          organization={event.speakerOrg}
                          accent={event.color}
                          paused={hoveredEventId === event.id || shareOpenId === event.id}
                        />
                        <div className="events-card__share">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={e => { e.stopPropagation(); setShareOpenId(current => current === event.id ? null : event.id); }}
                                aria-label={shareOpenId === event.id ? "Close sharing options" : `Share ${event.title}`}
                                aria-controls={`event-share-${event.id}`}
                                aria-expanded={shareOpenId === event.id}
                                className="events-card__share-trigger"
                                data-visible={showShareTrigger || undefined}
                              >
                                <Share2 size={14} aria-hidden="true" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="left">
                              {shareOpenId === event.id ? "Close sharing options" : "Share"}
                            </TooltipContent>
                          </Tooltip>
                          {shareOpenId === event.id && (
                            <div
                              id={`event-share-${event.id}`}
                              className="events-card__share-panel"
                              onClick={e => e.stopPropagation()}
                            >
                              <p>Event URL</p>
                              <div className="events-card__share-url">
                                <Link size={13} aria-hidden="true" />
                                <code>{eventUrl(event)}</code>
                                <button type="button" onClick={() => copyEventUrl(event)}>
                                  <Copy size={11} aria-hidden="true" /> {copiedEventId === event.id ? "Copied" : "Copy"}
                                </button>
                              </div>
                              <div className="events-card__share-networks">
                                <button type="button" onClick={() => shareEvent("linkedin", event)}>
                                  <Linkedin size={11} aria-hidden="true" /> LinkedIn
                                </button>
                                <button type="button" onClick={() => shareEvent("facebook", event)}>
                                  <Facebook size={11} aria-hidden="true" /> Facebook
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="events-card__actions">
                      {buyTicketsFor(event) ? (
                        <BuyTicketsButton
                          slug={event.slug}
                          className="events-button events-button--accent"
                          iconSize={14}
                          onClick={clickEvent => clickEvent.stopPropagation()}
                        />
                      ) : showRegisteredStatus ? (
                        <div className="events-card__registered-status" role="status">
                          <CheckCircle size={14} aria-hidden="true" />
                          <span><small>You’re all set</small> Already registered</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="events-button events-button--accent"
                          disabled={Boolean(cardAwaitingDecision) && !cardCanReapply}
                          aria-disabled={Boolean(cardAwaitingDecision) && !cardCanReapply}
                          style={cardAwaitingDecision && !cardCanReapply ? AWAITING_DECISION_CTA_STYLE : undefined}
                          onClick={e => {
                            e.stopPropagation();
                            if (eventIsPast) {
                              handlePastEventRecording(event);
                              return;
                            }
                            if (attendeeZoomLink) {
                              window.open(attendeeZoomLink, "_blank", "noopener,noreferrer");
                              return;
                            }
                            openEvent(event);
                            handleRegisterClick(event);
                          }}
                        >
                          {eventIsPast
                            ? hasPaidAccess
                              ? <><PlayCircle size={14} aria-hidden="true" /> Watch the highlight reel!</>
                              : <><LockKeyhole size={14} aria-hidden="true" /> Past Event - Watch a recording</>
                            : attendeeZoomLink
                              ? <><ExternalLink size={14} aria-hidden="true" /> Join on Zoom</>
                              : cardCanReapply
                                ? <><Ticket size={14} aria-hidden="true" /> Reapply</>
                                : cardAwaitingDecision === "pending"
                                ? <><Clock size={14} aria-hidden="true" /> Awaiting Approval</>
                                : cardAwaitingDecision === "waitlisted"
                                  ? <><Clock size={14} aria-hidden="true" /> On the Waitlist</>
                                  : <><Ticket size={14} aria-hidden="true" /> Reserve Your Seat</>}
                        </button>
                      )}
                      </div>
                    </div>

                    {/*
                      The preview, not the destination. Clicking the card goes to the
                      event's own page; this stays on the calendar and opens the
                      modal, so someone scanning the list can check one event without
                      losing their place in it.

                      An icon with the name on hover rather than a labelled button:
                      the row it used to sit in belongs to the one action a visitor
                      came for, and a second worded control there competed with it.
                    */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="events-card__quicklook"
                          aria-label={`Quick Look at ${event.title}`}
                          onClick={e => { e.stopPropagation(); openEvent(event); }}
                        >
                          <ZoomIn size={15} aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left">Quick Look</TooltipContent>
                    </Tooltip>

                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ── INSIDE THE CHI EXPERIENCE ── */}
      <section id="experience" className="events-experience">
        <div className="container">
          <div className="events-section-heading">
            <h2>Inside the CHI Experience</h2>
            <p>Real conversations. Real connections. Real impact.</p>
          </div>
          <div className="events-experience__grid">
            {EXPERIENCE_TILES.map((tile, index) => (
              <RouterLink
                key={tile.image}
                href="/community"
                className="events-experience__card"
                data-tile={index + 1}
                aria-label={`${tile.kicker}: ${tile.headline} Explore the CHI community`}
              >
                <ResponsiveImage src={tile.image} alt={tile.alt} loading="lazy" decoding="async" maxWidth={640} sizes="(max-width: 700px) 100vw, 33vw" />
                <div className="events-experience__copy">
                  <span>{tile.kicker}</span>
                  <h3>{tile.headline}</h3>
                  <p>{tile.detail}</p>
                  <div className="events-experience__indicator" aria-hidden="true">
                    <i /> Discover the room
                  </div>
                </div>
              </RouterLink>
            ))}
          </div>
        </div>
      </section>

      {/*
        ── MEMBERSHIP CTA ──

        This was a partnership pitch. On a page whose whole purpose is getting
        someone to a room, the next step is an account, not a collaboration
        enquiry — and partnership already has a home of its own at
        /collaborate-with-us, where someone looking for it will go.

        `source=calendar-membership` marks where the signup began, so the value of
        this placement is measurable rather than assumed.
      */}
      {/* Translucent rather than the solid #0D1220 it used to be: the band still
          lifts away from the sections around it, but an opaque fill here would
          punch a flat rectangle through the page photograph. */}
      <section style={{ background: "rgba(13,18,32,0.72)", padding: "5rem 0" }}>
        <div className="container text-center">
          <SectionBadge>Membership</SectionBadge>
          <h2 style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(1.5rem, 3vw, 2.5rem)", letterSpacing: "-0.05em", color: "white", marginBottom: "1.25rem" }}>
            More than an event. A community.
          </h2>
          <p style={{ color: "#94A3B8", fontSize: "var(--chi-body)", lineHeight: 1.75, maxWidth: "560px", margin: "0 auto 2.5rem", fontFamily: "'Inter', sans-serif" }}>
            Create your profile to register for events, meet the people in the room, and keep the conversation going after it ends.
          </p>
          <button
            type="button"
            onClick={() => navigate("/create-account?source=calendar-membership")}
            className="chi-btn-primary flex items-center gap-2 mx-auto w-fit"
          >
            Create Your Account <CtaIcon icon={Users} />
          </button>
        </div>
      </section>

      <MembershipBanner
        headingLead="Register first,"
        headingAccent="every time."
        copy={"Priority event registration, full member bios, verified profile links, and the member badge — membership is free."}
      />

      <Footer />


      {/* ═══════════════════════════════════════════════════════════════
          MODALS
      ═══════════════════════════════════════════════════════════════ */}
      {modal && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(0,0,0,0.75)",
            backdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "1rem",
          }}
          onClick={e => { if (e.target === e.currentTarget) closeAll(); }}
        >

          {/* ── EVENT DETAIL MODAL ── */}
          {modal === "event" && selectedEvent && (
            <div
              style={{
                background: "#0D1220",
                border: `1px solid ${selectedEvent.color}30`,
                borderRadius: "1.25rem",
                width: "100%",
                maxWidth: "680px",
                maxHeight: "90vh",
                position: "relative",
                boxShadow: `0 0 60px ${selectedEvent.color}20`,
                // Only the details area scrolls, so the title and the register
                // action stay in view however long the event copy runs.
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Top accent */}
              <div style={{ height: "3px", background: `linear-gradient(90deg, ${selectedEvent.color}, transparent)`, borderRadius: "1.25rem 1.25rem 0 0", flexShrink: 0 }} />

              {/* Uploaded/generated art wins; deterministic branded art keeps every
                  Quick Look visually complete while artwork is still being prepared. */}
              {selectedEventArt && (
                <ResponsiveImage
                  src={selectedEventArt.url}
                  alt={`Promotional artwork for ${selectedEvent.title}`}
                  maxWidth={640}
                  sizes="min(680px, 100vw)"
                  style={{
                    display: "block",
                    width: "100%",
                    height: "clamp(110px, 22vh, 190px)",
                    objectFit: "cover",
                    objectPosition: "center top",
                    flexShrink: 0,
                    borderBottom: "1px solid rgba(255,255,255,0.07)",
                  }}
                />
              )}

              {/* ── STATIC HEADER ── */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "1rem",
                  padding: "1.5rem 2rem 1.25rem",
                  borderBottom: "1px solid rgba(255,255,255,0.07)",
                  flexShrink: 0,
                }}
              >
                  <div className="calendar-event-modal__header-content" style={{ display: "flex", alignItems: "center", gap: "0.9rem", minWidth: 0 }}>
                    <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: "0.3rem",
                        background: selectedEvent.type === "virtual" ? "rgba(0,212,255,0.1)" : "rgba(245,166,35,0.1)",
                        border: selectedEvent.type === "virtual" ? "1px solid rgba(0,212,255,0.25)" : "1px solid rgba(245,166,35,0.25)",
                        borderRadius: "100px", padding: "0.25rem 0.75rem",
                        fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", fontWeight: 600,
                        color: selectedEvent.type === "virtual" ? "#00D4FF" : "#F5A623",
                      }}>
                        {selectedEvent.type === "virtual" ? <Video size={11} /> : <Building2 size={11} />}
                        {selectedEvent.type === "virtual" ? "Virtual Event" : "In-Person Event"}
                      </span>
                      {registeredIds.includes(selectedEvent.id) && (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: "0.3rem",
                          background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)",
                          borderRadius: "100px", padding: "0.25rem 0.75rem",
                          fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", fontWeight: 600,
                          color: "#22C55E",
                        }}>
                          <CheckCircle size={11} /> You're Registered
                        </span>
                      )}
                      {(() => {
                        // Share control rides the tag row: an icon at rest, revealing
                        // its label on hover so it reads as an action, not decoration.
                        const copied = copiedEventId === selectedEvent.id;
                        const active = copied || copyPillHovered;
                        const accent = selectedEvent.color;
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              const eventUrl = `${window.location.origin}/events/${selectedEvent.slug}`;
                              navigator.clipboard?.writeText(eventUrl).catch(() => {});
                              setCopiedEventId(selectedEvent.id);
                              window.setTimeout(() => setCopiedEventId(null), 2200);
                            }}
                            onMouseEnter={() => setCopyPillHovered(true)}
                            onMouseLeave={() => setCopyPillHovered(false)}
                            onFocus={() => setCopyPillHovered(true)}
                            onBlur={() => setCopyPillHovered(false)}
                            aria-label={`Copy the link to ${selectedEvent.title}`}
                            title="Copy event link"
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "0.3rem",
                              background: active ? `${accent}1F` : "rgba(255,255,255,0.03)",
                              border: `1px solid ${active ? `${accent}59` : "rgba(255,255,255,0.10)"}`,
                              borderRadius: "100px", padding: "0.25rem 0.75rem",
                              fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", fontWeight: 600,
                              letterSpacing: "0.06em",
                              color: active ? accent : "#64748B",
                              cursor: "pointer", whiteSpace: "nowrap",
                              transition: "color 160ms ease, background 160ms ease, border-color 160ms ease",
                            }}
                          >
                            {copied ? <CheckCircle size={11} /> : <Copy size={11} />}
                            {active && (copied ? "COPIED" : "COPY")}
                          </button>
                        );
                      })()}
                    </div>
                    <h2 style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.32rem", letterSpacing: "-0.04em", color: "white", lineHeight: 1.15 }}>
                      {selectedEvent.title}
                    </h2>
                    {selectedEvent.subtitle && (
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.9rem", color: "#64748B", marginTop: "0.375rem" }}>
                        {selectedEvent.subtitle}
                      </p>
                    )}
                    <p style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.6rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 600, color: selectedEvent.color }}>
                      <Calendar size={13} aria-hidden="true" />
                      {selectedEvent.displayDate}
                      <span style={{ color: "#64748B", fontWeight: 500 }}>· {selectedEvent.time}</span>
                    </p>
                    </div>
                  </div>
                  <button onClick={closeAll} aria-label="Close event details" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "0.5rem", padding: "0.5rem", color: "white", cursor: "pointer", flexShrink: 0 }}>
                    <X size={18} />
                  </button>
              </div>

              {/* ── SCROLLABLE DETAILS ── */}
              <div style={{ padding: "1.5rem 2rem", overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
                {/* Meta grid */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.5rem" }}>
                  {[
                    { icon: <Calendar size={14} />, label: "Date", val: selectedEvent.displayDate },
                    { icon: <Clock size={14} />, label: "Time", val: selectedEvent.time },
                    { icon: <MapPin size={14} />, label: "Location", val: selectedEvent.location },
                    { icon: <User size={14} />, label: "Speaker", val: `${selectedEvent.speaker} · ${selectedEvent.speakerOrg}` },
                  ].map(m => (
                    <div key={m.label} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "0.625rem", padding: "0.875rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem", color: selectedEvent.color, marginBottom: "0.375rem" }}>
                        {m.icon}
                        <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.035em" }}>{m.label}</span>
                      </div>
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.8rem", color: "#CBD5E1" }}>{m.val}</p>
                    </div>
                  ))}
                </div>

                {selectedEvent.speakerBio && (
                  <div style={{ background: `${selectedEvent.color}08`, border: `1px solid ${selectedEvent.color}20`, borderRadius: "0.75rem", padding: "1.25rem", marginBottom: "1.5rem" }}>
                    <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.035em", color: selectedEvent.color, marginBottom: "0.625rem" }}>
                      Speaker Bio
                    </p>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", color: "#94A3B8", lineHeight: 1.75, margin: 0 }}>
                      {selectedEvent.speakerBio}
                    </p>
                  </div>
                )}

                {/* Topic — the event's own description, in a container matching the other sections */}
                {selectedEvent.description?.trim() && (
                  <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "0.75rem", padding: "1.25rem", marginBottom: "1.75rem" }}>
                    <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.035em", color: selectedEvent.color, marginBottom: "0.875rem" }}>
                      Topic
                    </p>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", color: "#CBD5E1", lineHeight: 1.8, margin: 0 }}>
                      {selectedEvent.description}
                    </p>
                  </div>
                )}

                {/* What you'll learn */}
                <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "0.75rem", padding: "1.25rem", marginBottom: "1.75rem" }}>
                  <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.035em", color: selectedEvent.color, marginBottom: "0.875rem" }}>
                    What You'll Get
                  </p>
                  <ul style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {selectedEvent.highlights.map(h => (
                      <li key={h} style={{ display: "flex", alignItems: "flex-start", gap: "0.625rem" }}>
                        <Star size={12} style={{ color: selectedEvent.color, flexShrink: 0, marginTop: "3px" }} />
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", color: "#CBD5E1" }}>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>

              {/* ── STATIC FOOTER — the register action stays visible ── */}
              <div
                style={{
                  padding: "1.15rem 2rem",
                  borderTop: "1px solid rgba(255,255,255,0.07)",
                  background: "rgba(8,12,24,0.72)",
                  flexShrink: 0,
                }}
              >
                {registeredIds.includes(selectedEvent.id) && !selectedAttendeeZoomLink && !isPastCalendarEvent(selectedEvent, eventClock) ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: "0.75rem", padding: "1rem 1.25rem" }}>
                    <CheckCircle size={20} style={{ color: "#22C55E" }} />
                    <div>
                      <p style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 700, color: "#22C55E", fontSize: "0.86rem", letterSpacing: "-0.025em" }}>You're registered!</p>
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", color: "#94A3B8", lineHeight: 1.5 }}>Your secure Zoom link will appear here and in My Events after successful registration once the meeting is ready.</p>
                    </div>
                  </div>
                ) : (
                  /*
                    Two actions, in the order they are wanted: registering is why
                    someone opened this, and the public page is the thing they share
                    with a colleague afterwards. Register keeps the primary treatment
                    so the secondary link cannot compete with it.
                  */
                  <div className="calendar-event-modal__actions">
                    {buyTicketsFor(selectedEvent) ? (
                      <BuyTicketsButton slug={selectedEvent.slug} style={{ padding: "0.875rem", fontSize: "0.9rem" }} />
                    ) : (
                    <button
                      onClick={() => {
                        if (isPastCalendarEvent(selectedEvent, eventClock)) {
                          handlePastEventRecording(selectedEvent);
                          return;
                        }
                        handleRegisterClick();
                      }}
                      disabled={modalAwaitingDecision}
                      aria-disabled={modalAwaitingDecision}
                      className="chi-btn-primary flex items-center justify-center gap-2"
                      style={{ padding: "0.875rem", fontSize: "0.9rem", ...(modalAwaitingDecision ? AWAITING_DECISION_CTA_STYLE : {}) }}
                    >
                      {isPastCalendarEvent(selectedEvent, eventClock)
                        ? hasPaidAccess
                          ? <>Watch the highlight reel! <CtaIcon icon={PlayCircle} /></>
                          : <>Past Event - Watch a recording <CtaIcon icon={LockKeyhole} /></>
                        : selectedAttendeeZoomLink
                          ? <>Join on Zoom <CtaIcon icon={Video} /></>
                          : selectedCanReapply
                            ? <>Reapply <CtaIcon icon={Ticket} /></>
                            : selectedAwaitingDecision === "pending"
                            ? <>Awaiting Approval <CtaIcon icon={Clock} /></>
                            : selectedAwaitingDecision === "waitlisted"
                              ? <>On the Waitlist <CtaIcon icon={Clock} /></>
                              : <>Register for This Event <CtaIcon icon={Ticket} /></>}
                    </button>
                    )}
                    {/*
                      Routed through `navigate` like every other link on this page,
                      rather than a raw anchor: this is an internal page, and a full
                      document load would discard the calendar's filter and scroll
                      state on the way back.
                    */}
                    <button
                      type="button"
                      className="calendar-event-modal__public-link"
                      onClick={() => {
                        closeAll();
                        navigate(`/events/${selectedEvent.slug}`);
                      }}
                    >
                      <ExternalLink size={15} aria-hidden="true" />
                      View event public URL
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── PAST EVENT RECORDING MEMBERSHIP GATE ── */}
          {modal === "recordingGate" && selectedEvent && (
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="recording-gate-title"
              aria-describedby="recording-gate-description"
              style={{
                background: "linear-gradient(155deg, #11182A 0%, #0B1020 62%, #171026 100%)",
                border: "1px solid rgba(249,168,212,.28)",
                borderRadius: "1.25rem",
                width: "100%",
                maxWidth: "500px",
                padding: "2rem",
                position: "relative",
                boxShadow: "0 28px 90px rgba(0,0,0,.55), 0 0 50px rgba(233,30,140,.1)",
              }}
            >
              <div style={{ height: 3, background: "linear-gradient(90deg, #E91E8C, #F5A623, #00D4FF)", borderRadius: "1.25rem 1.25rem 0 0", position: "absolute", inset: "0 0 auto" }} />
              <button
                type="button"
                onClick={closeAll}
                aria-label="Close membership invitation"
                style={{ position: "absolute", top: "1.15rem", right: "1.15rem", background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", borderRadius: ".5rem", padding: ".45rem", color: "white", cursor: "pointer" }}
              >
                <X size={17} aria-hidden="true" />
              </button>

              <div style={{ width: 54, height: 54, borderRadius: "50%", display: "grid", placeItems: "center", color: "#F9A8D4", background: "rgba(233,30,140,.11)", border: "1px solid rgba(249,168,212,.3)", marginBottom: "1.2rem" }}>
                <LockKeyhole size={23} aria-hidden="true" />
              </div>
              <p style={{ margin: "0 0 .55rem", color: "#F5A623", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".68rem", fontWeight: 800, letterSpacing: ".11em", textTransform: "uppercase" }}>
                Exclusive member content
              </p>
              <h3 id="recording-gate-title" style={{ margin: 0, maxWidth: "22rem", color: "#FFF", fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontSize: "clamp(1.45rem, 5vw, 2rem)", fontWeight: 700, letterSpacing: "-.045em", lineHeight: 1.08 }}>
                Keep the conversation going.
              </h3>
              <p id="recording-gate-description" style={{ margin: ".85rem 0 0", color: "#B8C3D4", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.65 }}>
                Join the CHI Alliance free to watch past-event recordings, revisit practical highlights, and turn each conversation into ongoing learning.
              </p>

              <div style={{ display: "grid", gap: ".65rem", margin: "1.35rem 0 1.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: ".7rem", color: "#E2E8F0", fontFamily: "'Inter', sans-serif", fontSize: ".82rem" }}>
                  <PlayCircle size={17} color="#F9A8D4" aria-hidden="true" /> Event recordings and highlight reels
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: ".7rem", color: "#E2E8F0", fontFamily: "'Inter', sans-serif", fontSize: ".82rem" }}>
                  <GraduationCap size={17} color="#7DEAFF" aria-hidden="true" /> Exclusive micro-learning for busy leaders
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: ".7rem", color: "#E2E8F0", fontFamily: "'Inter', sans-serif", fontSize: ".82rem" }}>
                  <Users size={17} color="#F5A623" aria-hidden="true" /> Member-only community content and connections
                </div>
              </div>

              <div className="chi-cta-pair chi-cta-pair--stacked">
                <button
                  type="button"
                  className="chi-btn-primary w-full"
                  style={{ width: "100%", padding: ".9rem" }}
                  onClick={() => {
                    closeAll();
                    navigate("/membership");
                  }}
                >
                  Join free <CtaIcon icon={ArrowRight} />
                </button>
                {!isAuthenticated && (
                  <button
                    type="button"
                    className="chi-btn-outline w-full"
                    style={{ width: "100%", padding: ".8rem" }}
                    onClick={() => {
                      closeAll();
                      navigate("/login?redirect=/calendar-of-events");
                    }}
                  >
                    Already a member? Sign in
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── SIGN IN MODAL ── */}
          {modal === "signin" && (
            <div style={{ background: "#0D1220", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "1.25rem", width: "100%", maxWidth: "420px", padding: "2rem", position: "relative" }}>
              <div style={{ height: "3px", background: "linear-gradient(90deg, #E91E8C, #F5A623)", borderRadius: "1.25rem 1.25rem 0 0", position: "absolute", top: 0, left: 0, right: 0 }} />
              <button onClick={closeAll} style={{ position: "absolute", top: "1.25rem", right: "1.25rem", background: "rgba(255,255,255,0.06)", border: "none", borderRadius: "0.375rem", padding: "0.375rem", color: "white", cursor: "pointer" }}>
                <X size={16} />
              </button>
              <div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg, #E91E8C, #F5A623)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
                  <User size={22} style={{ color: "white" }} />
                </div>
                <h3 style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.2rem", letterSpacing: "-0.04em", color: "white" }}>Sign In</h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", color: "#64748B", marginTop: "0.375rem" }}>Sign in to register for events and manage your schedule.</p>
              </div>
              <div className="chi-cta-pair chi-cta-pair--stacked" style={{ marginBottom: "1rem" }}>
                <button onClick={() => selectedEvent && navigate(getInternalEventRegistrationLoginUrl(selectedEvent))} className="chi-btn-primary w-full" style={{ padding: "0.875rem" }}>
                  Sign In Securely <CtaIcon icon={User} />
                </button>
              {/*
                The other half of this decision: someone who reached a sign-in
                prompt without an account needs a way forward, not just a way in.
                Event Detail already offered it; the calendar did not, so a
                first-time visitor hit a dead end at the point of highest intent.

                The helper carries the selected event through signup, so they land
                back on the event they were registering for rather than the
                calendar's front page.
              */}
              <button
                type="button"
                onClick={() => selectedEvent && navigate(getInternalEventRegistrationCreateAccountUrl(selectedEvent))}
                className="chi-btn-outline w-full"
                style={{
                  width: "100%", padding: "0.8rem", fontSize: "0.78rem",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
                }}
              >
                Create a Member Account <CtaIcon icon={Users} />
              </button>
              </div>
              <div style={{ textAlign: "center" }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", color: "#64748B" }}>You will be guided through account access before registration can continue.</span>
              </div>
            </div>
          )}

          {/* ── REGISTER CONFIRM MODAL ── */}
          {modal === "register" && selectedEvent && (
            <div style={{ background: "#0D1220", border: `1px solid ${selectedEvent.color}30`, borderRadius: "1.25rem", width: "100%", maxWidth: "480px", padding: "2rem", position: "relative" }}>
              <div style={{ height: "3px", background: `linear-gradient(90deg, ${selectedEvent.color}, transparent)`, borderRadius: "1.25rem 1.25rem 0 0", position: "absolute", top: 0, left: 0, right: 0 }} />
              <button onClick={closeAll} style={{ position: "absolute", top: "1.25rem", right: "1.25rem", background: "rgba(255,255,255,0.06)", border: "none", borderRadius: "0.375rem", padding: "0.375rem", color: "white", cursor: "pointer" }}>
                <X size={16} />
              </button>
              <div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: `${selectedEvent.color}20`, border: `1px solid ${selectedEvent.color}40`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
                  <Ticket size={22} style={{ color: selectedEvent.color }} />
                </div>
                <h3 style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.2rem", letterSpacing: "-0.04em", color: "white" }}>Confirm Registration</h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", color: "#64748B", marginTop: "0.375rem" }}>You're registering as <strong style={{ color: "white" }}>{user?.name ?? "your signed-in account"}</strong></p>
              </div>

              {/* Event summary */}
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "0.75rem", padding: "1.25rem", marginBottom: "1.5rem" }}>
                <h4 style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "0.94rem", letterSpacing: "-0.025em", color: "white", marginBottom: "0.75rem" }}>{selectedEvent.title}</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", color: "#94A3B8" }}>
                    <Calendar size={13} style={{ color: selectedEvent.color }} /> {selectedEvent.displayDate}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", color: "#94A3B8" }}>
                    <Clock size={13} style={{ color: selectedEvent.color }} /> {selectedEvent.time}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", color: "#94A3B8" }}>
                    <MapPin size={13} style={{ color: selectedEvent.color }} /> {selectedEvent.location}
                  </span>
                </div>
              </div>

              <button disabled={registrationMutation.isPending} onClick={handleConfirmRegister} className="chi-btn-primary w-full flex items-center justify-center gap-2" style={{ padding: "0.875rem", marginBottom: "0.75rem" }}>
                {registrationMutation.isPending ? "Confirming…" : "Confirm My Registration"} <CtaIcon icon={CheckCircle} />
              </button>
              <button onClick={() => setModal("event")} style={{ width: "100%", background: "none", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "0.5rem", padding: "0.75rem", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", cursor: "pointer" }}>
                Go Back
              </button>
            </div>
          )}

          {/* ── CONFIRMED MODAL ── */}
          {modal === "confirmed" && selectedEvent && (
            <div style={{ background: "#0D1220", border: "1px solid rgba(34,197,94,0.3)", borderRadius: "1.25rem", width: "100%", maxWidth: "420px", padding: "2.5rem", textAlign: "center", position: "relative" }}>
              <div style={{ height: "3px", background: "linear-gradient(90deg, #22C55E, #00D4FF)", borderRadius: "1.25rem 1.25rem 0 0", position: "absolute", top: 0, left: 0, right: 0 }} />
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(34,197,94,0.15)", border: "2px solid rgba(34,197,94,0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem" }}>
                <CheckCircle size={30} style={{ color: "#22C55E" }} />
              </div>
              <h3 style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.35rem", letterSpacing: "-0.045em", color: "white", marginBottom: "0.75rem" }}>You're In!</h3>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", color: "#94A3B8", lineHeight: 1.7, marginBottom: "0.5rem" }}>
                You've successfully registered for
              </p>
              <p style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "0.92rem", letterSpacing: "-0.025em", color: "white", marginBottom: "1.5rem" }}>
                {selectedEvent.title}
              </p>
              <div style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.15)", borderRadius: "0.625rem", padding: "0.875rem", marginBottom: "1.75rem" }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.8rem", color: "#94A3B8" }}>
                  Your registration is saved to your signed-in account. Add the event to your calendar and we’ll see you there!
                </p>
              </div>
              <div className="chi-cta-pair" style={{ alignItems: "stretch" }}>
                <button
                  onClick={() => { setModal("myevents"); }}
                  className="chi-btn-primary flex-1 flex items-center justify-center gap-1.5"
                  style={{ padding: "0.75rem", fontSize: "0.8rem" }}
                >
                  My Events <CtaIcon icon={Ticket} size={14} />
                </button>
                <button
                  onClick={closeAll}
                  className="chi-btn-outline flex-1 flex items-center justify-center"
                  style={{ padding: "0.75rem", fontSize: "0.8rem" }}
                >
                  Browse More Events
                </button>
              </div>
            </div>
          )}

          {/* ── MY EVENTS DASHBOARD ── */}
          {modal === "myevents" && (
            <div style={{ background: "#0D1220", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "1.25rem", width: "100%", maxWidth: "620px", maxHeight: "90vh", overflowY: "auto", position: "relative" }}>
              <div style={{ height: "3px", background: "linear-gradient(90deg, #E91E8C, #F5A623)", borderRadius: "1.25rem 1.25rem 0 0" }} />
              <div style={{ padding: "2rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.75rem" }}>
                  <div>
                    <h3 style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.2rem", letterSpacing: "-0.04em", color: "white" }}>My Events</h3>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", color: "#64748B", marginTop: "0.25rem" }}>
                      Signed in as <strong style={{ color: "#E91E8C" }}>{user?.name ?? "your account"}</strong>
                    </p>
                  </div>
                  <button onClick={closeAll} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "0.5rem", padding: "0.5rem", color: "white", cursor: "pointer" }}>
                    <X size={18} />
                  </button>
                </div>

                {myEvents.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
                    <Ticket size={40} style={{ color: "#475569", margin: "0 auto 1rem" }} />
                    <p style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 600, fontSize: "1rem", letterSpacing: "-0.025em", color: "#64748B", marginBottom: "0.5rem" }}>No events yet</p>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", color: "#475569", marginBottom: "1.5rem" }}>Browse upcoming events and register to see them here.</p>
                    <button onClick={closeAll} className="chi-btn-primary" style={{ padding: "0.75rem 1.75rem" }}>Browse Events</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
                    {myEvents.map(event => {
                      const attendeeZoomLink = attendeeZoomLinksBySlug.get(event.slug);
                      return (
                      <div
                        key={event.id}
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          border: `1px solid ${event.color}30`,
                          borderRadius: "0.875rem",
                          padding: "1.25rem",
                          display: "flex",
                          gap: "1rem",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ width: 48, height: 48, borderRadius: "0.625rem", background: `${event.color}18`, border: `1px solid ${event.color}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {event.type === "virtual" ? <Video size={18} style={{ color: event.color }} /> : <Building2 size={18} style={{ color: event.color }} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                            <h4 style={{ fontFamily: "'Syne', 'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "0.9rem", letterSpacing: "-0.025em", color: "white" }}>{event.title}</h4>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: "100px", padding: "0.15rem 0.5rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.65rem", fontWeight: 600, color: "#22C55E", flexShrink: 0 }}>
                              <CheckCircle size={9} /> Registered
                            </span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginTop: "0.5rem" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.72rem", color: "#64748B" }}>
                              <Calendar size={11} style={{ color: event.color }} /> {event.displayDate}
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.72rem", color: "#64748B" }}>
                              <Clock size={11} style={{ color: event.color }} /> {event.time}
                            </span>
                          </div>
                          <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.72rem", color: "#64748B", marginTop: "0.375rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                            <MapPin size={11} style={{ color: event.color }} /> {event.location}
                          </p>
                          {event.type === "virtual" && (
                            attendeeZoomLink ? (
                              <button type="button" onClick={() => window.open(attendeeZoomLink, "_blank", "noopener,noreferrer")} style={{ marginTop: ".85rem", display: "inline-flex", alignItems: "center", gap: ".45rem", border: "1px solid rgba(103,232,249,.45)", borderRadius: ".5rem", background: "rgba(0,180,216,.16)", color: "#A5F3FC", padding: ".55rem .78rem", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: ".73rem", cursor: "pointer" }}>
                                <Video size={14} /> Join on Zoom
                              </button>
                            ) : (
                              <p style={{ margin: ".75rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".74rem", lineHeight: 1.45 }}>Your secure Zoom link will display here after successful registration once the meeting is ready.</p>
                            )
                          )}
                        </div>
                      </div>
                    );
                    })}
                  </div>
                )}

                {myEvents.length > 0 && (
                  <div style={{ marginTop: "1.5rem", padding: "1rem", background: "rgba(233,30,140,0.06)", border: "1px solid rgba(233,30,140,0.15)", borderRadius: "0.75rem" }}>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.8rem", color: "#94A3B8", textAlign: "center" }}>
                      Your current registrations are secured to your signed-in account. For virtual events, a protected Zoom link appears here and on the event page after successful registration.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
